import { defineStore } from "pinia";

import { useDataStore, useCrudStore, useAuthStore, useUIStore, useSchemaStore } from "@/stores";
import { cloneDeep, isEqual } from "lodash";
import {
  JOINT_TEMPLATE,
  ENTITY_TEMPLATE,
  NEW_BENEFICIAL_OWNER_TEMPLATE,
  FIELDS_WITH_ADDITIONAL_INFO,
  SUITABILITY_QUESTIONNAIRE_GROUPS,
  CUSTODIAN_NOT_LISTED_OPTION,
} from "@/constants";
import {
  extractSigningLinkFromResponse,
  getObjectDifferences,
  isRetirementAccountType,
} from "@/utilities";
import useFileUtility from "@/composables/useFileUtility";
import { syncContactPersonProperties } from "@/composables/useAnalytics";

export const usePurchaseFlowStore = defineStore("purchaseFlowStore", {
  state: () => ({
    // Purchase Flow Details
    current_step: 0,
    current_substep: 1,
    investment_steps: null,
    current_investment: {
      amount: 25000,
      type: "Individual",
      is_compounded: true,
      payment_method: "ACH",
      distribution_method: "ACH",
      funding_account: null,
      distribution_account: null,
      voided_check: false,
      status: "Created",
    },
    mailing_same_as_residential: false,
    joint_residential_same_as_primary: false,

    selected_offering: null,
    selected_rollover: null,

    // joint - entity - beneficial owner contacts
    active_joint_contact: null,
    active_entity_account: null,

    //beneficial owner
    beneficial_owner_to_add: cloneDeep(NEW_BENEFICIAL_OWNER_TEMPLATE),

    // voided check upload
    voided_check: null,

    // previous objects for change detection
    previous_investment: null,
    previous_contact: null,
    previous_joint_contacts: null,
    previous_entity_accounts: null,
    previous_beneficial_owners: null,

    // change investment metrics
    is_change_investment_modal_open: false,
    has_investment_changed: false,
    investment_snapshot: null,

    // signing link
    signing_link: null,
    signing_link_generated_at: null,
    is_generating_signing_link: false,
    signing_link_invalidated: false,

    //cloned schema
    accredited_owner_contact_personal_info_schema: [],
    accredited_joint_contact_personal_info_schema: [],
    accredited_ben_owner_contact_personal_info_schema: [],
    suitability_questions: [],
    entity_information: [],
    read_only_after_investments: ["ssn", "ein", "first_name", "last_name", "name", "email"],
    fields_with_additional_info: FIELDS_WITH_ADDITIONAL_INFO,

    //track investment progress
    is_fast_track_modal_open: false,
    can_fast_track: false,
    has_fast_track_been_shown: false,
    PNCAcctsExpiring: [],

    //disclaimer
    is_show_disclaimer_modal: false,
    suitability_review_error: null,
    suitability_issue_state: "none", // 'none' | 'has_issues' | 'fixed'

    // step-order A/B experiment
    funding_first: false,
  }),
  getters: {
    currentStep(state) {
      if (state.current_step > 0 && state.investment_steps) {
        return state.investment_steps[state.current_step - 1];
      }
      return null;
    },
    regDTier(state) {
      const dataStore = useDataStore();

      // Calculate total: sum of everything in a settled/funded status + current entered amount (if not a rollover, or if rollover and entered amount exceeds rollover amount)
      const allFundedPlusSettled = dataStore.investments.reduce((acc, inv) => {
        return inv.status === "Funded" || inv.status === "Settled" ? acc + inv.amount : acc;
      }, 0);

      // If rollover selected, only count excess over the parent's amount
      let currentAmount = 0;
      const enteredAmount = state.current_investment?.amount
        ? Number(String(state.current_investment.amount).replace(/,/g, ""))
        : 0;

      if (state.selected_rollover) {
        const rolloverAmount = Number(state.selected_rollover.amount || 0);
        currentAmount = enteredAmount > rolloverAmount ? enteredAmount - rolloverAmount : 0;
      } else {
        currentAmount = enteredAmount;
      }

      const total = allFundedPlusSettled + currentAmount;

      if (total >= 1000000) return 3;
      if (total >= 500000) return 2;
      return 1;
    },
    signedInvestmentStatus: () => {
      const dataStore = useDataStore();
      if (!dataStore.investments?.length) {
        return {
          has_signed_investment: false,
          has_signed_joint_investment: false,
          has_signed_entity_investment: false,
          has_signed_regd: false,
          has_signed_s1: false,
        };
      }

      const signed = dataStore.investments.filter(
        (inv) => inv.signature_date && inv.status !== "Canceled"
      );

      return {
        has_signed_investment: signed.length > 0,
        has_signed_joint_investment: signed.some((inv) => inv.type === "Joint"),
        has_signed_entity_investment: signed.some((inv) => inv.type === "Entity"),
        has_signed_regd: signed.some((inv) => inv.offering_type === "Reg D"),
        has_signed_s1: signed.some((inv) => inv.offering_type === "S1"),
      };
    },
    rolloverGetter: () => {
      const dataStore = useDataStore();
      const allInvestments = dataStore?.investments || [];

      // Find pending rollovers (unsigned children) that haven't been
      // reflected in the parent's rollover_available yet
      const pendingRolloversByParent = {};
      for (const inv of allInvestments) {
        if (inv.status === "Pending" && inv.parent_investment && !inv.signature_date) {
          if (!pendingRolloversByParent[inv.parent_investment]) {
            pendingRolloversByParent[inv.parent_investment] = 0;
          }
          pendingRolloversByParent[inv.parent_investment] += inv.amount || 0;
        }
      }

      const investments_for_rollover = allInvestments
        .filter((investment) => {
          const passes =
            investment.signature_date &&
            investment.status === "Settled" &&
            investment.able_to_rollover === true;
          return passes;
        })
        .map((investment) => {
          const pendingAmount = pendingRolloversByParent[investment.record_id] || 0;
          const adjusted_rollover_available = (investment.rollover_available || 0) - pendingAmount;

          return {
            ...investment,
            rollover_available: adjusted_rollover_available,
          };
        })
        .filter((investment) => {
          const passes = investment.rollover_available >= 1000;
          return passes;
        });

      const total_rollover_available = investments_for_rollover.reduce(
        (sum, investment) => sum + (investment.rollover_available || 0),
        0
      );

      return {
        has_rollovers_available: investments_for_rollover.length > 0,
        total_rollover_available,
        investments_for_rollover,
      };
    },
    investmentMinimumAmount() {
      if (this.selected_rollover) return 1000;

      const offering = this.selected_offering;
      const offeringType = offering?.type;

      if (offeringType === "Reg D") {
        return this.signedInvestmentStatus?.has_signed_regd ? 1000 : 25000;
      }

      if (offeringType === "S1") {
        if (offering?.is_short_term) return 1000;
        return this.signedInvestmentStatus?.has_signed_s1 ? 1000 : 5000;
      }

      return 1000;
    },
    isCurrentInvestmentBelowMinimum() {
      const amount = Number(String(this.current_investment?.amount ?? "0").replace(/,/g, "")) || 0;
      return amount > 0 && amount < this.investmentMinimumAmount;
    },
    isSigningLinkReady(state) {
      if (!state.signing_link || state.signing_link_invalidated) return false;
      const elapsed = Date.now() - state.signing_link_generated_at;
      return elapsed >= 8000;
    },
    signingLinkTimeRemaining(state) {
      if (!state.signing_link || state.signing_link_invalidated) return null;
      const elapsed = Date.now() - state.signing_link_generated_at;
      return Math.max(0, 8000 - elapsed);
    },
  },
  actions: {
    async initializePurchaseStore() {
      const dataStore = useDataStore();
      const crudStore = useCrudStore();

      this.active_joint_contact = dataStore.joint_contacts?.length
        ? dataStore.joint_contacts[0]
        : JOINT_TEMPLATE;
      this.active_entity_account = dataStore.entity_accounts?.length
        ? dataStore.entity_accounts[0]
        : ENTITY_TEMPLATE;

      this.signing_link = null;
      this.signing_link_generated_at = null;

      this.cloneNeededSchema();

      // use a database call here since investments aren't set to pending until the thank-you screen
      const unsignedInvestment = await crudStore.findOne("investments", [
        { field_name: "signature_date", operator: "is", value: null },
        { field_name: "user_id", operator: "eq", value: dataStore.user_id },
        { field_name: "status", operator: "not.in", value: ["Pending", "Pending S1", "Canceled"] },
      ]);

      if (unsignedInvestment) {
        // console.log("Found unsigned investment:", unsignedInvestment);
        this.current_investment = { ...this.current_investment, ...unsignedInvestment };
        this.selected_offering = dataStore.offerings.find(
          (o) => o.record_id === unsignedInvestment.offering_id
        );
        if (unsignedInvestment.parent_investment && unsignedInvestment.is_rollover) {
          this.selected_rollover = dataStore.investments.find(
            (i) => i.record_id === unsignedInvestment.parent_investment
          );
        }
        if (!this.selected_offering && unsignedInvestment.offering_id) {
          this.selected_offering = this.resolveOffering(unsignedInvestment.offering_id);
        }
        this.canFastTrackForType(unsignedInvestment.type);
      } else {
        this.current_investment = { ...this.current_investment };
      }

      this.applySignedInvestmentFieldRestrictions();
      this.setOriginalObjects();
      this.setInvestmentStepsForType();
    },
    setInvestmentStepsForType(fundingFirst) {
      if (fundingFirst !== undefined) this.funding_first = fundingFirst;
      const dataStore = useDataStore();
      const { type } = this.current_investment ?? {};

      const fundingStep = {
        label: "Funding and Distribution",
        name: "Funding and Distribution",
        sub_steps: ["Funding your investment", "Distribution"],
      };

      // Base steps that always come first
      const baseSteps = [
        { label: "How much would you like to invest?", name: "Amount" },
        { label: "How would you like to receive returns?", name: "Payment Frequency" },
        { label: "How much return would you like?", name: "Term and Yield" },
        { label: "Select the type of investment", name: "Investment Type" },
        ...(this.funding_first && type && !isRetirementAccountType(type) ? [fundingStep] : []),
        {
          label: "Personal Information",
          name: "Personal Information",
          sub_steps: [
            "Personal Information",
            "Residentials Address",
            "Mailing Address",
            "ID Information",
          ],
        },
        {
          label: "Suitability Questionnaire",
          name: "Suitability Questionnaire",
          sub_steps: SUITABILITY_QUESTIONNAIRE_GROUPS.map((g) => g.name),
        },
      ];

      // Conditional middle steps
      const conditionalSteps = [];

      if (type === "Joint") {
        const subSteps = this.active_joint_contact?.record_id
          ? [
              "Joint Contact Selection",
              "Joint Information",
              "Residential Address",
              "ID Information",
            ]
          : ["Joint Information", "Residential Address", "ID Information"];
        conditionalSteps.push({
          label: "Joint Contact Selection",
          name: "Joint Information",
          sub_steps: subSteps,
        });
      }

      if (type === "Entity") {
        const subSteps = this.active_entity_account?.record_id
          ? ["Entity Selection", "Entity Information", "Entity Address", "Beneficial Owners"]
          : ["Entity Information", "Entity Address", "Beneficial Owners"];
        conditionalSteps.push({
          label: "Entity Selection",
          name: "Entity Information",
          sub_steps: subSteps,
        });
      }

      if (this.selected_offering?.type === "Reg D") {
        conditionalSteps.push({
          label: "Accreditation Details",
          name: "Accreditation",
          sub_steps: ["Acknowledgements", "Upload Documents"],
        });
      }

      if (type && !isRetirementAccountType(type) && !this.funding_first) {
        conditionalSteps.push(fundingStep);
      } else if (type && isRetirementAccountType(type)) {
        conditionalSteps.push({
          label: "IRA Details",
          name: "IRA Details",
        });
      }

      // Final steps that always come last
      const finalSteps = [
        // { label: "Finalize your investment", name: "Finalize Investment" },
        {
          label:
            dataStore.s1_offerings_in_quiet_period && this.selected_offering?.type === "S1"
              ? "Review and Submit"
              : "Review and Sign",
          name: "Review and Sign",
        },
      ];

      this.investment_steps = [...baseSteps, ...conditionalSteps, ...finalSteps];
    },
    setOriginalObjects() {
      const dataStore = useDataStore();
      this.previous_investment = cloneDeep(this.current_investment);
      this.previous_contact = cloneDeep(dataStore.contact);
      this.previous_joint_contacts = cloneDeep(dataStore.joint_contacts);
      this.previous_entity_accounts = cloneDeep(dataStore.entity_accounts);
      this.previous_beneficial_owners = cloneDeep(
        this.getUniqueContacts([
          dataStore.contact,
          ...(dataStore.joint_contacts || []),
          ...(dataStore.beneficial_owner_contacts || []),
        ])
      );
    },
    async saveInvestmentProgress() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      try {
        if (this.selected_offering) {
          this.current_investment.offering_id = this.selected_offering.record_id;
          this.current_investment.offering_name = this.selected_offering.name;
          this.current_investment.offering_type = this.selected_offering.type;
          this.current_investment.offering_issuer = this.selected_offering.issuer;
          this.current_investment.interest_rate = this.selected_offering.interest_rate;
          // Always derive from the offering. is_short_term is otherwise set independently
          // (offering cards, discarded metric changes, the regDTier watcher), which let it
          // drift out of sync — e.g. a Reg D investment flagged short term, a combination
          // that has no signing template and blows up signing link generation.
          this.current_investment.is_short_term = this.selected_offering.is_short_term === true;
        }

        // Set owner contact full name
        this.current_investment.owner_contact_full_name = dataStore.owner_full_name;

        if (this.current_investment.amount) {
          const amount =
            typeof this.current_investment.amount === "string"
              ? Number(this.current_investment.amount.replace(/,/g, ""))
              : this.current_investment.amount;
          this.current_investment.bonds = amount / 1000;
          this.current_investment.discounted_bond_amount = amount;
          this.current_investment.current_nav = amount;
        }

        if (
          ["Check", "Wire", "Existing Investment"].includes(this.current_investment.payment_method)
        ) {
          this.current_investment.funding_account = null;
        }

        if (["Check", "Wire"].includes(this.current_investment.distribution_method)) {
          this.current_investment.distribution_account = null;
        }

        if (this.current_investment.type === "Entity") {
          this.current_investment.entity_account_id = this.active_entity_account?.record_id;
        } else {
          this.current_investment.entity_account_id = null;
        }

        if (this.current_investment.type === "Joint") {
          this.current_investment.joint_contact_id = this.active_joint_contact?.record_id;
        } else {
          this.current_investment.joint_contact_id = null;
        }

        if (this.selected_rollover) {
          this.current_investment.is_rollover = true;
          this.current_investment.type = this.selected_rollover.type;
          this.current_investment.parent_investment = this.selected_rollover.record_id;
          this.current_investment.effective_date = this.selected_rollover.maturity_date;
          this.current_investment.payment_method = "Existing Investment";
          this.current_investment.custodian_account_number =
            this.selected_rollover.custodian_account_number;
          this.current_investment.custodian_id = this.selected_rollover.custodian_id;
        } else {
          if (this.current_investment.payment_method === "Existing Investment") {
            this.current_investment.payment_method = "ACH";
          }
          this.current_investment.is_rollover = false;
          this.current_investment.parent_investment = null;
          this.current_investment.effective_date = null;
          this.current_investment.custodian_account_number = null;
          this.current_investment.custodian_id = null;
        }

        if (!this.current_investment.record_id) {
          // Guard: check DB for an existing unsigned investment before inserting
          const existingUnsigned = await crudStore.findOne("investments", [
            { field_name: "signature_date", operator: "is", value: null },
            { field_name: "user_id", operator: "eq", value: dataStore.user_id },
            {
              field_name: "status",
              operator: "not.in",
              value: ["Pending", "Pending S1", "Canceled"],
            },
          ]);

          if (existingUnsigned) {
            // Adopt the existing record, then update it with current fields
            this.current_investment.record_id = existingUnsigned.record_id;
            this.previous_investment = cloneDeep(this.current_investment);

            await crudStore.update(
              "investments",
              [{ field_name: "record_id", operator: "eq", value: existingUnsigned.record_id }],
              this.current_investment
            );
          } else {
            this.current_investment = dataStore.setDefaultFields(this.current_investment);
            await crudStore.insert("investments", [this.current_investment]);
          }
        } else {
          const hasChanges = !isEqual(this.previous_investment, this.current_investment);
          if (!hasChanges) {
            // console.log("No changes detected in investment. Skipping save.");
            return;
          }

          const differences = getObjectDifferences(
            this.previous_investment,
            this.current_investment
          );
          // console.log("Differences detected in investment:", differences);

          this.previous_investment = cloneDeep(this.current_investment);
          await crudStore.update(
            "investments",
            [{ field_name: "record_id", operator: "eq", value: this.current_investment.record_id }],
            this.current_investment
          );
        }

        // make sure dataStore is updated with latest investment data for any dependent calculations
        const index = dataStore.investments.findIndex(
          (investment) => investment.record_id === this.current_investment?.record_id
        );
        if (index !== -1) {
          dataStore.investments[index] = cloneDeep(this.current_investment);
        } else {
          dataStore.investments.push(cloneDeep(this.current_investment));
        }

        // console.log("Investment progress saved.");
      } catch (err) {
        throw new Error(`Failed to save investment progress: ${err.message}`);
      }
    },
    async saveOwnerProgress(isChangeInvestment = false) {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();

      try {
        if (!isChangeInvestment) {
          dataStore.contact.current_step = this.getCurrentStepForMarketing();
        }
        const hasChanges = !isEqual(this.previous_contact, dataStore.contact);

        if (!hasChanges) {
          // console.log("No changes detected in owner contact. Skipping save.");
          return;
        }

        const differences = getObjectDifferences(this.previous_contact, dataStore.contact);
        // console.log("Differences detected in owner contact:", differences);

        const personPropsChanged =
          this.previous_contact?.date_of_birth !== dataStore.contact?.date_of_birth ||
          this.previous_contact?.state !== dataStore.contact?.state;

        this.previous_contact = cloneDeep(dataStore.contact);
        const owner_contact = dataStore.contact;

        const accountUpdate = {
          name: `${owner_contact.first_name} ${owner_contact.last_name}`,
          email: owner_contact.email,
          phone: owner_contact.phone,
          street1: owner_contact.street1,
          street2: owner_contact.street2,
          city: owner_contact.city,
          state: owner_contact.state,
          zip_code: owner_contact.zip_code,
        };

        await Promise.all([
          crudStore.update(
            "contacts",
            [{ field_name: "record_id", operator: "eq", value: owner_contact.record_id }],
            owner_contact
          ),
          crudStore.update(
            "accounts",
            [{ field_name: "record_id", operator: "eq", value: dataStore.account.record_id }],
            accountUpdate
          ),
        ]);

        if (personPropsChanged) syncContactPersonProperties(dataStore.contact);

        // Ensure the account object is updated in memory state
        dataStore.account = {
          ...dataStore.account,
          ...accountUpdate,
        };

        // console.log("Owner contact and account progress saved.");
      } catch (err) {
        throw new Error(`Failed to save owner contact progress: ${err.message}`);
      }
    },
    async saveJointProgress() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      try {
        // Insert case - no record_id means it's a new joint contact
        if (!this.active_joint_contact?.record_id) {
          this.active_joint_contact = dataStore.setDefaultFields(
            this.active_joint_contact,
            "contacts"
          );

          await crudStore.insert("contacts", [this.active_joint_contact]);

          dataStore.joint_contacts = [
            ...(dataStore.joint_contacts || []),
            this.active_joint_contact,
          ];

          // Update previous for future change detection
          this.previous_joint_contacts = cloneDeep(dataStore.joint_contacts);

          // console.log("Joint contact inserted.");
        } else {
          const previous_active_joint = this.previous_joint_contacts?.find(
            (jc) => jc.record_id === this.active_joint_contact.record_id
          );
          const hasChanges = !isEqual(previous_active_joint, this.active_joint_contact);
          if (hasChanges) {
            const differences = getObjectDifferences(
              previous_active_joint,
              this.active_joint_contact
            );
            // console.log("Differences detected in active joint contact:", differences);
          }
          if (!hasChanges) {
            // console.log("No changes detected in active joint contact. Skipping save.");
            return;
          }

          await crudStore.update(
            "contacts",
            [
              {
                field_name: "record_id",
                operator: "eq",
                value: this.active_joint_contact.record_id,
              },
            ],
            this.active_joint_contact
          );

          // Replace the contact in dataStore array with updated version
          const index = dataStore.joint_contacts.findIndex(
            (jc) => jc.record_id === this.active_joint_contact.record_id
          );
          if (index !== -1) {
            dataStore.joint_contacts[index] = cloneDeep(this.active_joint_contact);
          }

          // Update previous for future change detection
          this.previous_joint_contacts = cloneDeep(dataStore.joint_contacts);
          // console.log("Joint contact updated.");
        }
      } catch (err) {
        throw new Error(`Failed to save joint contact progress: ${err.message}`);
      }
    },
    async saveEntityProgress() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      try {
        // Insert case - no record_id means it's a new entity account
        if (!this.active_entity_account.record_id) {
          this.active_entity_account = dataStore.setDefaultFields(
            this.active_entity_account,
            "accounts"
          );

          await crudStore.insert("accounts", [this.active_entity_account]);
          dataStore.entity_accounts = [
            ...(dataStore.entity_accounts || []),
            this.active_entity_account,
          ];

          // Update previous for future change detection
          this.previous_entity_accounts = cloneDeep(dataStore.entity_accounts);

          // console.log("Entity account inserted.");
        } else {
          const previous_active_entity = this.previous_entity_accounts?.find(
            (ea) => ea.record_id === this.active_entity_account.record_id
          );

          const hasChanges = !isEqual(previous_active_entity, this.active_entity_account);
          if (hasChanges) {
            const differences = getObjectDifferences(
              previous_active_entity,
              this.active_entity_account
            );
            // console.log("Differences detected in active entity account:", differences);
          }
          if (!hasChanges) {
            // console.log("No changes detected in active entity account. Skipping save.");
            return;
          }

          await crudStore.update(
            "accounts",
            [
              {
                field_name: "record_id",
                operator: "eq",
                value: this.active_entity_account.record_id,
              },
            ],
            this.active_entity_account
          );

          // Replace the contact in dataStore array with updated version
          const index = dataStore.entity_accounts.findIndex(
            (ea) => ea.record_id === this.active_entity_account.record_id
          );
          if (index !== -1) {
            dataStore.entity_accounts[index] = cloneDeep(this.active_entity_account);
          }

          // Update previous for future change detection
          this.previous_entity_accounts = cloneDeep(dataStore.entity_accounts);

          // console.log("Entity account updated.");
        }
      } catch (err) {
        throw new Error(`Failed to save entity account progress: ${err.message}`);
      }
    },
    async saveBeneficialOwnersProgress() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      const entityId = this.active_entity_account.record_id;

      if (!entityId) {
        // console.log("No entity ID found. Skipping beneficial owners save.");
        return;
      }

      try {
        const contactsToUpdate = [];

        // Get all unique contacts (including owner)
        const allContacts = this.getUniqueContacts([
          dataStore.contact,
          ...(dataStore.joint_contacts || []),
          ...(dataStore.beneficial_owner_contacts || []),
        ]);

        // Find contacts whose entity_ids changed
        for (const contact of allContacts) {
          const previousContact = this.previous_beneficial_owners?.find(
            (c) => c.record_id === contact.record_id
          );

          // If no previous version exists, check if entity_id is currently present
          if (!previousContact) {
            if (contact.entity_ids?.includes(entityId)) {
              contactsToUpdate.push(contact);
            }
            continue;
          }

          // Compare previous and current entity_ids arrays
          const hadEntityId = previousContact.entity_ids?.includes(entityId);
          const hasEntityId = contact.entity_ids?.includes(entityId);

          if (hadEntityId !== hasEntityId) {
            contactsToUpdate.push(contact);
          }
        }

        if (contactsToUpdate.length === 0) {
          // console.log("No changes to beneficial owners. Skipping save.");
          return;
        }

        for (const contact of contactsToUpdate) {
          await crudStore.update(
            "contacts",
            [
              {
                field_name: "record_id",
                operator: "eq",
                value: contact.record_id,
              },
            ],
            { entity_ids: contact.entity_ids }
          );
        }

        // Update the snapshot for future change detection
        this.previous_beneficial_owners = cloneDeep(allContacts);

        // console.log(`Updated ${contactsToUpdate.length} beneficial owner contact(s).`);
      } catch (err) {
        throw new Error(`Failed to save beneficial owners progress: ${err.message}`);
      }
    },
    async saveAccreditationProgress() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();

      try {
        const isEntity = this.current_investment.type === "Entity";

        // Check for changes in accredited investor status
        const accreditedInvestorChanged =
          this.previous_contact?.are_you_an_accredited_investor !==
          dataStore.contact.are_you_an_accredited_investor;

        // Check for changes in acknowledgements
        let acknowledgementsChanged = false;
        if (isEntity) {
          const previousEntity = this.previous_entity_accounts?.find(
            (ea) => ea.record_id === this.active_entity_account.record_id
          );
          acknowledgementsChanged = !isEqual(
            previousEntity?.reg_d_entity_acknowledgements,
            this.reg_d_acknowledgements
          );
        } else {
          acknowledgementsChanged = !isEqual(
            this.previous_contact?.reg_d_personal_acknowledgements,
            this.reg_d_acknowledgements
          );
        }

        const hasChanges = accreditedInvestorChanged || acknowledgementsChanged;

        if (!hasChanges) {
          // console.log("No changes detected in accreditation. Skipping save.");
          return;
        }

        if (isEntity) {
          const updates = [];

          if (accreditedInvestorChanged) {
            updates.push(
              crudStore.update(
                "contacts",
                [{ field_name: "record_id", operator: "eq", value: dataStore.contact.record_id }],
                { are_you_an_accredited_investor: dataStore.contact.are_you_an_accredited_investor }
              )
            );
          }

          if (acknowledgementsChanged) {
            this.active_entity_account.reg_d_entity_acknowledgements = this.reg_d_acknowledgements;
            updates.push(
              crudStore.update(
                "accounts",
                [
                  {
                    field_name: "record_id",
                    operator: "eq",
                    value: this.active_entity_account.record_id,
                  },
                ],
                { reg_d_entity_acknowledgements: this.reg_d_acknowledgements }
              )
            );
          }

          await Promise.all(updates);

          // Update previous state after successful save
          this.previous_contact = cloneDeep(dataStore.contact);

          // Update the specific entity in previous_entity_accounts
          const index = this.previous_entity_accounts?.findIndex(
            (ea) => ea.record_id === this.active_entity_account.record_id
          );
          if (index !== -1) {
            this.previous_entity_accounts[index] = cloneDeep(this.active_entity_account);
          }
        } else {
          const contactUpdate = {};

          if (accreditedInvestorChanged) {
            contactUpdate.are_you_an_accredited_investor =
              dataStore.contact.are_you_an_accredited_investor;
          }

          if (acknowledgementsChanged) {
            contactUpdate.reg_d_personal_acknowledgements = this.reg_d_acknowledgements;
            dataStore.contact.reg_d_personal_acknowledgements = this.reg_d_acknowledgements;
          }

          await crudStore.update(
            "contacts",
            [{ field_name: "record_id", operator: "eq", value: dataStore.contact.record_id }],
            contactUpdate
          );

          // Update previous state after successful save
          this.previous_contact = cloneDeep(dataStore.contact);
        }

        // console.log("Accreditation progress saved.");
      } catch (err) {
        throw new Error(`Failed to save accreditation progress: ${err.message}`);
      }
    },
    async saveBeneficialOwner() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      try {
        let newBeneficialOwner = this.beneficial_owner_to_add;
        newBeneficialOwner = dataStore.setDefaultFields(newBeneficialOwner);
        this.previous_beneficial_owners.push(cloneDeep(newBeneficialOwner));
        dataStore.beneficial_owner_contacts.push(newBeneficialOwner);
        this.beneficial_owner_to_add = cloneDeep(NEW_BENEFICIAL_OWNER_TEMPLATE);

        await crudStore.insert("contacts", [newBeneficialOwner]);
      } catch (err) {
        throw new Error(`Failed to save beneficial owner: ${err.message}`);
      }
    },
    async generateSigningLink(investment_id) {
      // console.log("Generating signing link for investment ID:", investment_id);
      if (this.is_generating_signing_link) return;

      const authStore = useAuthStore();
      const UIStore = useUIStore();

      try {
        this.is_generating_signing_link = true;
        this.signing_link_invalidated = false;

        const response = await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/generate-signing-links`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              session_id: authStore.current_user.session_id,
              user_id: authStore.current_user?.id,
            },
            body: JSON.stringify({ investment_id }),
          }
        );

        if (!response.ok) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the subscription agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        const data = await response.json();
        const signingLink = extractSigningLinkFromResponse(data);

        if (!signingLink) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the subscription agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        this.signing_link = signingLink;
        this.signing_link_generated_at = Date.now();
        return signingLink;
      } catch (err) {
        console.error(err);
        return null;
      } finally {
        this.is_generating_signing_link = false;
      }
    },
    async getAccountsFromPlaid(accountType, accessToken) {
      const authStore = useAuthStore();
      const UIStore = useUIStore();
      const dataStore = useDataStore();
      const crudStore = useCrudStore();

      try {
        if (accountType === "Funding" || accountType === "Distribution") {
          UIStore.is_loading = true;
        }

        // Call Plaid identity match endpoint first, then make call to auth if successful
        const plaid_identity_response = await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/call-plaid-endpoint`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              "Access-Control-Allow-Origin": "*",
              user_id: authStore.current_user.id,
              session_id: authStore.current_user.session_id,
            },
            body: JSON.stringify({
              endpoint: "auth",
              data: {
                user_id: authStore.current_user.id,
                contact_id: dataStore.contact.record_id,
                accountType: accountType,
                plaid_access_token: accessToken,
                full_name: dataStore.contact.first_name + " " + dataStore.contact.last_name,
                phone_number: "+1" + dataStore.contact.phone.replace(/[()\s-]/g, ""), // format this
                email: dataStore.contact.email,
                street: dataStore.contact.street1,
                city: dataStore.contact.city,
                state: dataStore.contact.state.value,
                zip_code: dataStore.contact.zip_code,
                country: "US",
              },
            }),
          }
        );

        const plaidAccounts = await plaid_identity_response.json();

        let accountsToAdd = [];
        for (let [index, plaidAccount] of plaidAccounts.entries()) {
          let accountToAdd = {};
          const institution_response = await fetch(
            `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/call-plaid-endpoint`,
            {
              method: "POST",
              headers: {
                "content-type": "application/json",
                "Access-Control-Allow-Origin": "*",
                user_id: authStore.current_user.id,
                session_id: authStore.current_user.session_id,
              },
              body: JSON.stringify({
                endpoint: "get_institution_by_id",
                data: { institution_id: plaidAccount.institution_id },
              }),
            }
          );
          const institution = await institution_response.json();
          accountToAdd = dataStore.setDefaultFields(accountToAdd, "bankaccounts");

          accountToAdd.account_number = plaidAccount.account_number;
          accountToAdd.account_type =
            plaidAccount.subtype[0].toUpperCase() + plaidAccount.subtype.slice(1);
          accountToAdd.mask = plaidAccount.mask ? plaidAccount.mask : null;
          accountToAdd.financial_institution_name = institution.name;
          accountToAdd.institution_id = plaidAccount.institution_id;
          accountToAdd.routing_number = plaidAccount.routing_number;
          accountToAdd.plaid_account_id = plaidAccount.internal_account_id;
          accountToAdd.item_id = plaidAccount.item_id;
          accountToAdd.plaid_access_token = plaidAccount.plaid_access_token;
          accountToAdd.is_visible = true;
          accountToAdd.logo = institution.logo;
          accountToAdd.identity_verified = plaidAccount.identity_verified;
          accountToAdd.phone_score = plaidAccount.phone_score;
          accountToAdd.name_score = plaidAccount.name_score;
          accountToAdd.email_score = plaidAccount.email_score;

          // find existing bank account
          const existing_bank_account = await crudStore.findOne("bankaccounts", [
            { field_name: "user_id", operator: "eq", value: authStore.current_user.id },
            {
              field_name: "mask",
              operator: "eq",
              value: plaidAccount.mask,
            },
            { field_name: "institution_id", operator: "eq", value: accountToAdd.institution_id },
          ]);

          if (existing_bank_account) {
            if (accountToAdd.institution_id === "ins_13") {
              // replace existing PNC account for reconnection of expiring account
              let ba_update_status = await crudStore.update(
                "bankaccounts",
                [
                  { field_name: "user_id", operator: "eq", value: authStore.current_user.id },
                  {
                    field_name: "mask",
                    operator: "eq",
                    value: plaidAccount.mask,
                  },
                  {
                    field_name: "institution_id",
                    operator: "eq",
                    value: accountToAdd.institution_id,
                  },
                ],
                {
                  account_number: accountToAdd.account_number,
                  account_type: accountToAdd.account_type,
                  mask: accountToAdd.mask,
                  financial_institution_name: accountToAdd.financial_institution_name,
                  routing_number: accountToAdd.routing_number,
                  plaid_account_id: accountToAdd.plaid_account_id,
                  item_id: accountToAdd.item_id,
                  plaid_access_token: accountToAdd.plaid_access_token,
                  is_visible: true,
                  updated_date: new Date(),
                  created_date: new Date(),
                }
              );
              if (ba_update_status.status == 200) {
                this.PNCAcctsExpiring = this.PNCAcctsExpiring.filter(
                  (item) => item.account_number !== accountToAdd.account_number
                );
                //update PNC banks in state
                dataStore.bank_accounts = dataStore.bank_accounts.map((obj) =>
                  obj.account_number === accountToAdd.account_number
                    ? { ...obj, created_date: new Date() }
                    : obj
                );
              }
            } else {
              // replace existing bank accounts
              await crudStore.update(
                "bankaccounts",
                [
                  { field_name: "user_id", operator: "eq", value: authStore.current_user.id },
                  {
                    field_name: "account_number",
                    operator: "eq",
                    value: btoa(accountToAdd.account_number),
                  },
                  {
                    field_name: "institution_id",
                    operator: "eq",
                    value: accountToAdd.institution_id,
                  },
                ],
                {
                  account_number: accountToAdd.account_number,
                  account_type: accountToAdd.account_type,
                  mask: accountToAdd.mask,
                  financial_institution_name: accountToAdd.financial_institution_name,
                  routing_number: accountToAdd.routing_number,
                  plaid_account_id: accountToAdd.plaid_account_id,
                  item_id: accountToAdd.item_id,
                  plaid_access_token: accountToAdd.plaid_access_token,
                  is_visible: true,
                  updated_date: new Date(),
                }
              );
            }
          } else {
            // console.log("Inserting new bank account from Plaid:", accountToAdd);
            let ba_res = await crudStore.insert("bankaccounts", [accountToAdd]);
            if (ba_res.status == 200) {
              accountsToAdd.push(accountToAdd);
            }
          }
        }

        if (accountsToAdd.length) {
          if (accountsToAdd.length === 1) {
            this.setActiveBankAccount(accountsToAdd[0], "Distribution");
          } else {
            this.setActiveBankAccount(accountsToAdd[0], accountType);
          }
          dataStore.bank_accounts = [...accountsToAdd, ...dataStore.bank_accounts];
        }
      } catch (err) {
        console.error(err);
        UIStore.animateNotificationAlert({
          message: "There was an error retrieving your bank accounts. Please try again.",
          icon: "ShieldAlert",
          type: "error",
        });
      } finally {
        UIStore.is_loading = false;
      }
    },

    async setActiveBankAccount(bankingOption, accountType) {
      if (accountType === "Funding") {
        // If switching from an authorized card, fire-and-forget the hold release.
        if (this.current_investment.payment_method === "Card") {
          const piId = String(this.current_investment.funding_account ?? "").startsWith("pi_")
            ? this.current_investment.funding_account
            : null;
          if (piId) {
            const authStore = useAuthStore();
            const dataStore = useDataStore();
            // console.log("Cancelling Stripe PaymentIntent - setActiveBankAccount:", piId);
            await fetch(
              `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/checkout/payment-intent/${piId}/cancel`,
              {
                method: "POST",
                headers: {
                  user_id: authStore.current_user.id,
                  session_id: authStore.current_user.session_id,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ email: dataStore.contact.email }),
              }
            ).catch(() => {});
          }
        }
        this.current_investment.payment_method = "ACH";
        this.current_investment.funding_account = bankingOption.record_id;
      } else if (accountType === "Distribution") {
        this.current_investment.distribution_method = "ACH";
        this.current_investment.distribution_account = bankingOption.record_id;
        this.current_investment.voided_check = false;
      }
    },
    async uploadAccreditationDocs(files) {
      const authStore = useAuthStore();
      const dataStore = useDataStore();
      const { uploadFile_R2 } = useFileUtility();
      try {
        let newAccreditation = [];
        for (let file of files) {
          const accreditation = await uploadFile_R2({
            file,
            type: "Accreditation",
            details: {
              file_category: "Accreditation Support",
              visible_to_compliance: false,
              user_id: authStore.current_user.id,
              contact_id: dataStore.contact.record_id,
              account_id: dataStore.contact.account_id,
            },
          });
          newAccreditation.push(accreditation);
          if (!Array.isArray(dataStore.documents)) {
            dataStore.documents = [];
          }
          dataStore.documents.unshift(accreditation);
        }
        dataStore.accreditation_documents = [
          ...newAccreditation,
          ...(dataStore.accreditation_documents || []),
        ];
      } catch (err) {
        console.error("Error uploading accreditation documents:", err);
      }
    },
    async uploadSecuritiesEmployerDoc(file) {
      const authStore = useAuthStore();
      const dataStore = useDataStore();
      const { uploadFile_R2 } = useFileUtility();
      try {
        const doc = await uploadFile_R2({
          file,
          type: "General Documents",
          details: {
            file_category: "Securities Employer",
            user_id: authStore.current_user.id,
            contact_id: dataStore.contact.record_id,
            account_id: dataStore.contact.account_id,
          },
        });
        if (!Array.isArray(dataStore.documents)) dataStore.documents = [];
        dataStore.documents.unshift(doc);
        return doc;
      } catch (err) {
        console.error("Error uploading securities employer document:", err);
      }
    },
    async deleteSecuritiesEmployerDoc(doc) {
      const dataStore = useDataStore();
      const { deleteFile_R2 } = useFileUtility();
      try {
        await deleteFile_R2(doc);
        dataStore.documents = dataStore.documents.filter((d) => d.record_id !== doc.record_id);
      } catch (err) {
        console.error("Error deleting securities employer document:", err);
      }
    },
    async uploadVoidedCheck() {
      const authStore = useAuthStore();
      const dataStore = useDataStore();
      const crudStore = useCrudStore();
      const { uploadFile_R2 } = useFileUtility();
      try {
        if (this.voided_check[0]) {
          let file = this.voided_check[0];
          await uploadFile_R2({
            file,
            type: "General Documents",
            details: {
              file_category: "Financial Account Verification",
              visible_to_compliance: false,
              user_id: authStore.current_user.id,
              contact_id: dataStore.contact.record_id,
              account_id: dataStore.contact.account_id,
            },
          });
          let log = {
            step: "Uploaded Voided Check",
            substep: ``,
            status: ``,
            message: `Uploaded Voided Check`,
            url: "",
          };

          log = dataStore.setDefaultFields(log, "investoractivity");
          crudStore.insert("investoractivity", [log]);
        }
      } catch (err) {
        console.error(err);
      }
    },
    takeInvestmentSnapshot() {
      this.investment_snapshot = cloneDeep({
        amount: this.current_investment.amount,
        is_compounded: this.current_investment.is_compounded,
        is_short_term: this.current_investment.is_short_term,
        offering: this.selected_offering,
      });
      this.has_investment_changed = false;
    },
    getInvestmentChanges() {
      if (!this.investment_snapshot) return null;

      const changes = {
        amount: null,
        is_compounded: null,
        offering: null,
      };

      // Check amount
      const oldAmount =
        parseInt(String(this.investment_snapshot.amount).replace(/[^0-9]/g, "")) || 0;
      const newAmount =
        parseInt(String(this.current_investment.amount).replace(/[^0-9]/g, "")) || 0;
      if (oldAmount !== newAmount) {
        changes.amount = {
          old: this.investment_snapshot.amount,
          new: this.current_investment.amount,
        };
      }

      // Check is_compounded
      if (this.investment_snapshot.is_compounded !== this.current_investment.is_compounded) {
        changes.is_compounded = {
          old: this.investment_snapshot.is_compounded,
          new: this.current_investment.is_compounded,
        };
      }

      // Check offering
      const oldOfferingId = this.investment_snapshot.offering?.record_id;
      const newOfferingId = this.selected_offering?.record_id;
      if (oldOfferingId !== newOfferingId) {
        changes.offering = {
          old: this.investment_snapshot.offering,
          new: this.selected_offering,
        };
      }

      const hasAnyChange = Object.values(changes).some((c) => c !== null);
      return hasAnyChange ? changes : null;
    },
    async updateCompletedInvestment(investment) {
      const dataStore = useDataStore();
      const crudStore = useCrudStore();
      try {
        if (
          !isRetirementAccountType(investment.type) &&
          ((investment.status === "Pending S1" && !dataStore.s1_offerings_in_quiet_period) ||
            investment.status !== "Pending S1") &&
          !investment.signature_date
        ) {
          await crudStore.update(
            "investments",
            [
              { field_name: "record_id", operator: "eq", value: investment.record_id },
              { field_name: "signature_date", operator: "is", value: null },
            ],
            { status: "Pending" }
          );

          dataStore.investments = dataStore.investments.map((inv) =>
            inv.record_id === investment.record_id ? { ...investment, status: "Pending" } : inv
          );
        }

        if (
          investment.type === "Joint" &&
          ((investment.status === "Pending S1" && !dataStore.s1_offerings_in_quiet_period) ||
            investment.status !== "Pending S1")
        ) {
          await this.sendJointContactEmail(investment);
        }
      } catch (err) {
        console.error(`Error updating investment ${investment.record_id} status to pending:`, err);
      }
    },
    async setSignatureDateForIRA() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();

      try {
        if (isRetirementAccountType(this.current_investment.type)) {
          const now = new Date();
          this.current_investment.signature_date = new Date(
            Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
          );
        }

        if (this.current_investment.record_id) {
          await crudStore.update(
            "investments",
            [{ field_name: "record_id", operator: "eq", value: this.current_investment.record_id }],
            { signature_date: this.current_investment.signature_date }
          );
        }

        // Sync in-memory state
        const investment = dataStore.investments.find(
          (inv) => inv.record_id === this.current_investment.record_id
        );
        if (investment) {
          investment.status = this.current_investment.status;
        }
      } catch (err) {
        console.error(
          `Error setting signature date for IRA investment ${this.current_investment.record_id}:`,
          err
        );
      }
    },
    async setPendingS1Status() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();

      try {
        if (dataStore.s1_offerings_in_quiet_period && this.selected_offering?.type === "S1") {
          this.current_investment.status = "Pending S1";
        }
        await this.saveInvestmentProgress();
      } catch (err) {
        console.error(
          `Error setting pending S1 status for investment ${this.current_investment.record_id}:`,
          err
        );
      }
    },
    async sendJointContactEmail(investment) {
      const authStore = useAuthStore();
      try {
        await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/send-joint-signature-email`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              user_id: authStore.current_user.id,
              session_id: authStore.current_user.session_id,
            },
            body: JSON.stringify({
              investment: investment,
            }),
          }
        );
      } catch (err) {
        console.error(err);
      }
    },
    async applyProductParam(offeringType) {
      const dataStore = useDataStore();
      const isRegD = offeringType === "Reg D";

      const candidates = dataStore.offerings.filter((o) =>
        isRegD
          ? o.type === "Reg D" && o.tier === this.regDTier && !o.is_short_term
          : o.type === "S1" && !o.is_short_term
      );

      const best = candidates.sort((a, b) => b.interest_rate - a.interest_rate)[0];

      if (!best) {
        // No matching live offering found — fall back to the normal offerings screen
        // so the user isn't left stuck. The watcher will keep current_step at 0.
        console.warn(
          `[applyProductParam] No live ${offeringType} offering found. Falling back to offering selection screen.`
        );
        return;
      }

      // Override whatever initializePurchaseStore resolved
      this.selected_offering = best;
      this.current_investment.amount = isRegD ? 25000 : 5000;
      this.current_investment.is_short_term = false;
      this.current_investment.is_compounded = false;

      // Rebuild steps now that the offering type is known.
      // Reg D requires the Accreditation step — this must run before setting current_step.
      this.setInvestmentStepsForType();

      // Jump past the offering selection screen
      this.current_step = 1;

      await this.saveInvestmentProgress();
    },
    handleChangeURL(router, route) {
      try {
        const dataStore = useDataStore();

        function convertToSlug(text) {
          if (!text || typeof text !== "string") return "";
          return text
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^\w-]/g, "");
        }

        const query = {
          contact: dataStore.contact?.record_id || route.query.contact,
        };

        // Preserve external routing params so they survive Vue Router's replace calls.
        // `product` drives the new-experience routing (injected by PostHog or set manually).
        if (route.query.product) query.product = route.query.product;

        if (this.current_step === 0) {
          router.replace({
            query: {
              ...query,
              step: "select-offering",
            },
          });
          return;
        }

        if (this.selected_offering?.type) {
          query.offering = this.selected_offering.type;
        }

        if (this.current_investment?.type) {
          query.type = this.current_investment.type;
        }

        const stepData = this.investment_steps?.[this.current_step - 1];
        if (!stepData?.name) return;

        const stepSlug = convertToSlug(stepData.name);
        if (stepSlug) query.step = stepSlug;

        if (stepData.sub_steps?.length > 0 && this.current_substep > 0) {
          const substepName = stepData.sub_steps[this.current_substep - 1];
          if (substepName) {
            query.substep = convertToSlug(substepName);
          }
        }

        router.replace({ query });
      } catch (err) {
        console.warn("handleChangeURL failed:", err);
      }
    },
    applySignedInvestmentFieldRestrictions() {
      const { has_signed_investment, has_signed_joint_investment, has_signed_entity_investment } =
        this.signedInvestmentStatus;

      const processFields = (schemaGroups, shouldLock) => {
        schemaGroups.forEach((group) => {
          group.fields.forEach((field) => {
            const isLockTarget = this.read_only_after_investments.includes(field.field_name);
            const wasLocked = shouldLock && isLockTarget;

            if (wasLocked) {
              field.read_only = true;
            }

            const infoConfig = FIELDS_WITH_ADDITIONAL_INFO[field.field_name];
            if (infoConfig) {
              if (!infoConfig.onlyWhenReadOnly || wasLocked) {
                field.additional_info = infoConfig.text;
              }
            }
          });
        });
      };

      processFields(this.suitability_questions, false);
      processFields(this.accredited_owner_contact_personal_info_schema, has_signed_investment);
      processFields(
        this.accredited_joint_contact_personal_info_schema,
        has_signed_joint_investment
      );
      processFields(this.entity_information, has_signed_entity_investment);

      // Always lock email on owner contact schema
      this.accredited_owner_contact_personal_info_schema.forEach((group) => {
        group.fields.forEach((field) => {
          if (field.field_name === "email") {
            field.read_only = true;
            // Ensure additional_info is set since email is always locked here
            const infoConfig = FIELDS_WITH_ADDITIONAL_INFO[field.field_name];
            if (infoConfig) {
              field.additional_info = infoConfig.text;
            }
          }
        });
      });
    },
    getUniqueContacts(contacts) {
      if (!contacts || !Array.isArray(contacts)) return [];
      const seen = new Set();
      return contacts.filter((contact) => {
        if (!contact || seen.has(contact.record_id)) return false;
        seen.add(contact.record_id);
        return true;
      });
    },
    cloneNeededSchema() {
      const schemaStore = useSchemaStore();
      this.accredited_owner_contact_personal_info_schema = cloneDeep(
        schemaStore.accredited_owner_contact_personal_info_schema
      );
      this.accredited_joint_contact_personal_info_schema = cloneDeep(
        schemaStore.accredited_joint_contact_personal_info_schema
      );
      this.accredited_ben_owner_contact_personal_info_schema = cloneDeep(
        schemaStore.accredited_ben_owner_contact_personal_info_schema
      );
      this.entity_information = cloneDeep(schemaStore.entity_information);
      this.suitability_questions = cloneDeep(schemaStore.suitability_questions);
    },
    async updateLastCompletedStep() {
      const crudStore = useCrudStore();

      if (!this.current_investment?.record_id) return;

      try {
        this.current_investment.last_completed_step = this.current_step;
        this.current_investment.last_completed_substep = this.current_substep;

        // Sync the snapshot so saveInvestmentProgress doesn't detect these as changes
        if (this.previous_investment) {
          this.previous_investment.last_completed_step = this.current_step;
          this.previous_investment.last_completed_substep = this.current_substep;
        }

        await crudStore.update(
          "investments",
          [{ field_name: "record_id", operator: "eq", value: this.current_investment.record_id }],
          {
            last_completed_step: this.current_step,
            last_completed_substep: this.current_substep,
          }
        );
      } catch (err) {
        console.error("Failed to update last completed step:", err);
      }
    },
    isSuitabilityInfoComplete() {
      const dataStore = useDataStore();
      const contact = dataStore.contact;

      // Fail closed only if the schema itself hasn't loaded (a loading problem, not a
      // legitimate "this investor has no income fields" state).
      if (!this.suitability_questions?.length) return false;

      const incomeGroup = this.suitability_questions.find(
        (g) => g.group_name === "Income Information"
      );
      if (incomeGroup) {
        const incomeComplete = incomeGroup.fields.every((field) => {
          if (!field.is_required) return true;
          const value = contact?.suitability_info?.[field.field_name];
          return value !== null && value !== undefined && value !== "";
        });
        if (!incomeComplete) return false;
      }

      const questionnaireGroup = this.suitability_questions.find(
        (g) => g.group_name === "Questionnaire"
      );
      if (questionnaireGroup) {
        const allAnswered = questionnaireGroup.fields.every((field) => {
          const value = contact?.suitability_info?.[field.field_name];
          return value !== null && value !== undefined && value !== "";
        });
        if (!allAnswered) return false;
      }

      return true;
    },
    canFastTrackForType(type) {
      const dataStore = useDataStore();
      const schemaStore = useSchemaStore();

      const fail = (reason) => {
        // console.log("Fast-track failed:", reason);
        this.can_fast_track = false;
        return false;
      };

      // Validate owner contact against all personal info schema groups
      const ownerValid = this.accredited_owner_contact_personal_info_schema.every((group) => {
        const result = schemaStore.validateFields(
          dataStore.contact,
          group.fields,
          dataStore.contact.record_id,
          false
        );
        if (!result.proceed) {
          // console.log("  - owner personal info group", group.group_name, result.error_messages);
        }
        return result.proceed;
      });
      if (!ownerValid) return fail("owner personal info");

      if (!this.isSuitabilityInfoComplete()) return fail("suitability info incomplete");

      const empStatus = dataStore.contact?.suitability_info?.employment_status;
      if (
        (empStatus === "Employed" || empStatus === "Self-Employed") &&
        (!dataStore.contact?.occupation?.trim() || !dataStore.contact?.employer?.trim())
      ) {
        return fail("employment status requires occupation and employer");
      }

      // S1 financial suitability check moved to review step — validated there instead
      // if (this.selected_offering?.type === "S1") {
      //   const info = dataStore.contact.suitability_info || {};
      //   const income = Number(String(info.current_income || "").replace(/,/g, "")) || 0;
      //   const liquid = Number(String(info.current_liquid_net_worth || "").replace(/,/g, "")) || 0;
      //   const meetsIncome = income >= 70000 && liquid >= 70000;
      //   const meetsNetWorth = liquid >= 250000;
      //   if (!meetsIncome && !meetsNetWorth)
      //     return fail(`S1 financial suitability: income=${income}, liquid=${liquid}`);
      // }

      if (type === "Joint") {
        const joint = this.active_joint_contact;
        if (!joint?.record_id) return fail("no joint contact record_id");

        const jointValid = this.accredited_joint_contact_personal_info_schema.every((group) => {
          const result = schemaStore.validateFields(joint, group.fields, joint.record_id, false);
          if (!result.proceed) {
            // console.log("  - joint contact group", group.group_name, result.error_messages);
          }
          return result.proceed;
        });
        if (!jointValid) return fail("joint contact info");
      }

      if (type === "Entity") {
        const entity = this.active_entity_account;
        if (!entity?.record_id) return fail("no entity account record_id");

        const entityValid = this.entity_information.every((group) => {
          const result = schemaStore.validateFields(entity, group.fields, entity.record_id, false);
          if (!result.proceed) {
            // console.log("  - entity info group", group.group_name, result.error_messages);
          }
          return result.proceed;
        });
        if (!entityValid) return fail("entity info");

        const entityId = entity.record_id;
        const ownerIsBeneficial = dataStore.contact?.entity_ids?.includes(entityId);
        const hasBeneficialOwner = dataStore.beneficial_owner_contacts?.some((c) =>
          c.entity_ids?.includes(entityId)
        );
        if (!ownerIsBeneficial && !hasBeneficialOwner) return fail("no beneficial owners attached");
      }

      if (this.selected_offering?.type === "Reg D") {
        if (!dataStore.contact?.are_you_an_accredited_investor)
          return fail("not accredited investor");

        if (type === "Entity") {
          const acks = this.active_entity_account?.reg_d_entity_acknowledgements || [];
          const first14 = acks.slice(0, 14);
          const last2 = acks.slice(14);
          if (!first14.some(Boolean) || !(last2[0] || last2[1]))
            return fail("entity Reg D acknowledgements incomplete");
        } else {
          const acks = dataStore.contact?.reg_d_personal_acknowledgements || [];
          if (!acks.some(Boolean)) return fail("personal Reg D acknowledgements incomplete");
        }
      }

      if (isRetirementAccountType(type)) {
        const iraQ = dataStore.contact?.ira_questionnaire;
        if (!iraQ) return fail("no IRA questionnaire");

        const hasAnswered =
          iraQ.has_ira_account !== null &&
          iraQ.has_ira_account !== undefined &&
          iraQ.in_self_directed_ira !== null &&
          iraQ.in_self_directed_ira !== undefined &&
          iraQ.has_other_qualified_funds !== null &&
          iraQ.has_other_qualified_funds !== undefined;

        if (!hasAnswered) return fail("IRA questionnaire incomplete");
        if (iraQ.has_ira_account === true) {
          if (!iraQ.custodian) return fail("IRA custodian name missing");
          // "My custodian isn't listed" fills `custodian` without naming a custodian — the
          // institution name they typed instead is what completes the answer.
          if (iraQ.custodian === CUSTODIAN_NOT_LISTED_OPTION && !iraQ.retirement_funds_held_at)
            return fail("IRA custodian not listed and no institution name given");
        }
      }

      // console.log("Fast-track check passed for type:", type);
      this.can_fast_track = true;
      return true;
    },

    validateQuestionnaireForFastTrack(fields, contact) {
      const suitabilityInfo = contact?.suitability_info || {};

      // Fields excluded from the questionnaire UI — must stay in sync with
      // EXCLUDED_QUESTIONNAIRE_FIELDS in PF_SuitabilityInfo.vue
      const EXCLUDED_QUESTIONNAIRE_FIELDS = ["self_responsibility"];

      // All displayed questions must be answered — selection-based checks
      // (investment_objective, risk_willingness, time horizon, etc.) are
      // validated at the review/submit step, not here.
      return fields
        .filter((f) => !EXCLUDED_QUESTIONNAIRE_FIELDS.includes(f.field_name))
        .every((field) => {
          const value = suitabilityInfo[field.field_name];
          return value !== null && value !== undefined && value !== "";
        });
    },
    getCurrentStepForMarketing() {
      const completedNames = (this.investment_steps ?? [])
        .slice(0, this.current_step - 1)
        .map((s) => s.name);
      if (
        completedNames.includes("Funding and Distribution") ||
        completedNames.includes("IRA Details")
      ) {
        return "30";
      }
      if (completedNames.includes("Personal Information")) {
        return "20";
      }
      return "10";
    },
    resolveOffering(offeringId) {
      const dataStore = useDataStore();

      const highestRate = (offerings) =>
        offerings.reduce((best, o) => (o.interest_rate > best.interest_rate ? o : best));

      // 0. No offering ID at all — default to best S1
      if (!offeringId) {
        const s1Offerings = dataStore.offerings.filter((o) => o.type === "S1" && !o.is_short_term);
        return s1Offerings.length ? highestRate(s1Offerings) : null;
      }

      // 1. Try exact match first
      const exact = dataStore.offerings.find((o) => o.record_id === offeringId);
      if (exact) return exact;

      // 2. If we have the investment's offering metadata, use it to determine fallback
      const investment = this.current_investment;
      const offering_type = investment.offering_type;

      // 3. Reg A or unknown → default to best S1
      if (!offering_type || offering_type === "Reg A") {
        const s1Offerings = dataStore.offerings.filter((o) => o.type === "S1" && !o.is_short_term);
        return s1Offerings.length ? highestRate(s1Offerings) : null;
      }

      // 4. Legacy Reg D → match by tier + short-term, then highest interest rate
      if (offering_type === "Reg D") {
        const regDOfferings = dataStore.offerings.filter(
          (o) => o.type === "Reg D" && o.tier === this.regDTier && !o.is_short_term
        );

        if (regDOfferings.length) {
          const best = highestRate(regDOfferings);
          return best;
        }

        const s1Offerings = dataStore.offerings.filter((o) => o.type === "S1" && !o.is_short_term);
        return s1Offerings.length ? highestRate(s1Offerings) : null;
      }

      // 5. Any other type — try matching by type (highest rate), then best S1
      const typeOfferings = dataStore.offerings.filter(
        (o) => o.type === offering_type && !o.is_short_term
      );
      if (typeOfferings.length) {
        const best = highestRate(typeOfferings);
        return best;
      }

      const s1Offerings = dataStore.offerings.filter((o) => o.type === "S1" && !o.is_short_term);
      return s1Offerings.length ? highestRate(s1Offerings) : null;
    },
  },
});
